import { Input } from '@workspace/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { cn } from '@workspace/ui/lib/utils';
import { useState } from 'react';

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+57', country: 'CO', flag: '🇨🇴' },
  { code: '+593', country: 'EC', flag: '🇪🇨' },
  { code: '+380', country: 'UA', flag: '🇺🇦' },
  { code: '+54', country: 'AR', flag: '🇦🇷' },
  { code: '+56', country: 'CL', flag: '🇨🇱' },
  { code: '+51', country: 'PE', flag: '🇵🇪' },
  { code: '+58', country: 'VE', flag: '🇻🇪' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
];

export function PhoneInput({
  placeholder,
  required,
  value,
  onChange,
  hasError,
}: {
  name: string;
  placeholder: string;
  required: boolean;
  value: string;
  onChange: (value: string) => void;
  hasError: boolean;
}) {
  const [countryCode, setCountryCode] = useState(() => {
    if (value) {
      const matchedCode = COUNTRY_CODES.find((cc) =>
        value.startsWith(cc.code.replace('+', '')),
      );
      return matchedCode ? matchedCode.code : '+57';
    }
    return '+57';
  });

  const [phoneNumber, setPhoneNumber] = useState(() => {
    if (value && countryCode) {
      const codeWithoutPlus = countryCode.replace('+', '');
      return value.startsWith(codeWithoutPlus)
        ? value.substring(codeWithoutPlus.length)
        : value;
    }
    return '';
  });

  const handleCountryCodeChange = (newCode: string) => {
    setCountryCode(newCode);
    const codeWithoutPlus = newCode.replace('+', '');
    const fullNumber = phoneNumber
      ? `${codeWithoutPlus}${phoneNumber}`
      : codeWithoutPlus;
    onChange(fullNumber);
  };

  const handlePhoneNumberChange = (newNumber: string) => {
    const cleanNumber = newNumber.replace(/\D/g, '');
    setPhoneNumber(cleanNumber);
    const codeWithoutPlus = countryCode.replace('+', '');
    const fullNumber = `${codeWithoutPlus}${cleanNumber}`;
    onChange(fullNumber);
  };

  return (
    <div className="flex gap-2">
      <Select value={countryCode} onValueChange={handleCountryCodeChange}>
        <SelectTrigger
          className={cn(
            'dark:bg-background border-[#27272a] text-white text-sm h-8',
            {
              'border-red-500': hasError,
            },
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COUNTRY_CODES.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <span className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span>{country.code}</span>
                <span className="text-xs text-muted-foreground">
                  {country.country}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        placeholder={placeholder}
        value={phoneNumber}
        onChange={(e) => handlePhoneNumberChange(e.target.value)}
        className={`flex-1 ${hasError ? 'border-red-500' : ''}`}
        required={required}
      />
    </div>
  );
}
